<template>
  <Teleport to="body">
    <dialog-base :show="isShow()"
                 @hide="hide()"
                 :close-on-click="true"
                 :show-x="false"
                 :is-draggable="true"
                 size="lg"
                 test-id="card-featured-modal"
                 class="card-with-markup-container">
      <template #content>
        <div class="image-container" :class="markupLabels">
          <img ref="thumb" :src="thumbnail" @load="measureThumbnail" />
          <template v-for="(item, idx) in elementData.featuredresultspanel" :key="idx">
            <div class="bounding-box fr-overlay" :style="getBoundingBoxStyle(boxOf(item))">
              <highlight-result :element-data="item" :class="getMarkupClasses(item)">
                <markup-link :resultData="item" :resultType="item.resulttype" />
              </highlight-result>
            </div>
          </template>
        </div>
        <div data-testid="card-featured-close-btn" @click="hide()">
          <i class="bi bi-x-circle close-icon"></i>
        </div>
      </template>
    </dialog-base>
  </Teleport>
</template>


<script>
import { unref } from "vue";
import { mapGetters } from "vuex";
import { IMPACT_QUERY_DATE_FORMAT } from "@/constants";
import DialogBase from "@/components/modals/DialogBase.vue";
import MarkupLink from "@/components/card/MarkupLink.vue";
import HighlightResult from "@/components/card/HighlightResult.vue";

export default {
  name: "FeaturedResultsDialog",
  components: {
    DialogBase,
    MarkupLink,
    HighlightResult
  },
  data() {
    return {
      thumbnail: "",
      timeoutID: null,
      // Displayed size of the screenshot. The dialog constrains the image, so it is drawn
      // SMALLER than the panel's natural size (1100px wide on a typical SERP) — the stored
      // coordinates are page pixels and must be scaled by the same factor or every overlay
      // lands too far right and too low.
      renderedWidth: 0,
      renderedHeight: 0,
      naturalWidth: 0,   // screenshot's own pixel size — see measureThumbnail
      naturalHeight: 0,
    };
  },
  inject: {
    cardMetaRef: { from: 'cardMeta', default: () => ({}) }
  },
  props: {
    elementData: Object,
  },
  computed: {
    ...mapGetters("markup", { markupLabels: "markupLabels" }),
    cardMeta() {
      return unref(this.cardMetaRef) || {};
    },
  },
  methods: {
    async processFeaturedResult() {
      const date = this.$dateTime
        .fromISO(this.cardMeta.datetime)
        .toFormat(IMPACT_QUERY_DATE_FORMAT);
      const res = await this.$api.getFeaturedResultsScreenshot(this.cardMeta.tid, date);
      this.thumbnail = res?.data ? "data:image/png;base64," + res.data : "";
    },
    isShow() {
      return this.thumbnail !== "";
    },
    hide() {
      this.$emit("hide");
    },
    /**
     * The bounding box of the panel or one of its cards, as {x, y, width, height}.
     *
     * The parser writes snake_case — `css.bounding_box.{x,y,width,height}` — straight from
     * Playwright's `bounding_box()` (browsers-web-service serp/shared/featured_screenshot.py).
     * This file read PascalCase (`css.BoundingBox.{X,Y,Width,Height}`), which no poll has
     * ever produced: verified 2026-08-06 against browser-polled poll 115240560 and
     * API-polled polls alike. Every value came out `undefined`, so each overlay rendered as
     * `undefined px` and collapsed into a thin line under the screenshot.
     *
     * Both spellings are accepted so this cannot silently break again.
     */
    boxOf(node) {
      const css = node?.css;
      if (!css) return null;
      const box = css.bounding_box || css.BoundingBox;
      if (!box) return null;
      return {
        x: box.x ?? box.X ?? 0,
        y: box.y ?? box.Y ?? 0,
        width: box.width ?? box.Width ?? 0,
        height: box.height ?? box.Height ?? 0,
      };
    },
    measureThumbnail() {
      const img = this.$refs.thumb;
      if (!img) return;
      this.renderedWidth = img.offsetWidth;
      this.renderedHeight = img.offsetHeight;
      // See the card component: the screenshot's own size is the correct scale
      // denominator, because the stored panel box can disagree with the crop.
      this.naturalWidth = img.naturalWidth;
      this.naturalHeight = img.naturalHeight;
      // Keep measuring: the dialog settles (and can be dragged/resized) AFTER the
      // image's load event, so a single load-time measurement captures a mid-layout
      // size and every overlay is scaled by a stale ratio. The observer re-fires on
      // every size change and the reactive renderedWidth/Height recompute the boxes.
      if (!this._thumbObserver) {
        this._thumbObserver = new ResizeObserver(() => {
          this.renderedWidth = img.offsetWidth;
          this.renderedHeight = img.offsetHeight;
        });
        this._thumbObserver.observe(img);
      }
    },
    /**
     * The origin the card boxes are measured against (the screenshot is cropped to the
     * panel, so page coordinates must be shifted by the panel's own position).
     *
     * Derived from the CARD boxes when the panel's own css is absent: measured live
     * (2026-08-06) the dialog's elementData carried per-card css but no usable panel
     * css, so the origin subtraction was 0 and every box sat exactly one panel-offset
     * too far right and down — the hover box landed at left 1077/top 335 where the
     * card was at 1219/285, matching the no-subtraction prediction to the pixel. The
     * cards tile the panel, so their minimum x/y IS the panel origin.
     */
    overlayOrigin() {
      const p = this.boxOf(this.elementData);
      if (p && (p.width || p.height)) return p;
      const boxes = (this.elementData?.featuredresultspanel || [])
        .map((i) => this.boxOf(i)).filter(Boolean);
      if (boxes.length) {
        return { x: Math.min(...boxes.map((b) => b.x)),
                 y: Math.min(...boxes.map((b) => b.y)),
                 width: p?.width || 0, height: p?.height || 0 };
      }
      return { x: 0, y: 0, width: 0, height: 0 };
    },
    getBoundingBoxStyle(box) {
      if (!box) return {};
      const panel = this.overlayOrigin();
      const x_adjustment = panel.x || 0;
      const y_adjustment = panel.y || 0;

      // ...and the screenshot is displayed scaled to fit the dialog, so page pixels have
      // to be scaled by the same ratio. Before this, boxes were placed at raw page
      // coordinates over a shrunken image and drifted further off the further right and
      // further down the card sat.
      // Scale by the screenshot's own size, not the stored panel box: the two are
      // measured at different moments and can disagree (observed: css height 336 vs
      // image height 312 — every box skewed ~7% vertically until this used naturals).
      const srcW = this.naturalWidth || panel.width;
      const srcH = this.naturalHeight || panel.height;
      const xScale = this.renderedWidth && srcW ? this.renderedWidth / srcW : 1;
      const yScale = this.renderedHeight && srcH ? this.renderedHeight / srcH : 1;

      return {
        top: `${(box.y - y_adjustment) * yScale}px`,
        left: `${(box.x - x_adjustment) * xScale}px`,
        width: `${box.width * xScale}px`,
        height: `${box.height * yScale}px`,
        position: "absolute",
        cursor: "pointer",  // Change the cursor to indicate it's clickable
      };
    }
  },
  mounted() {
    this.processFeaturedResult();
  },
  beforeUnmount() {
    if (this._thumbObserver) this._thumbObserver.disconnect();
  },
};
</script>

<style lang="scss">
@import "@/scss/_markup.scss";


.card-with-markup-container {
  z-index: 100 !important;

  .image-container {
    position: relative;
    display: inline-block;
  }

  .bounding-box {
    pointer-events: auto;
    cursor: pointer;

    .highlight-element {
      height: 100%;
      opacity: .8;


      &:hover {
        border-color: black;
      }

      .markup-link-container {
        height: 100%;

        &.markup-link-text {
          color: transparent;
        }
      }
    }
  }

  .close-icon {
    cursor: pointer;
    position: absolute;
    top: 3px;
    right: 3px;
    font-size: 20px;
    color: #6c757d;
    z-index: 9999;
    pointer-events: auto;
  }

  .draggable-container {
    transform: scale(0.8);
    transform-origin: top left;
  }
}
</style>
